import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';
import { STATUS_CODE } from '@kc-console/core';

import { HOST_QUERY_KEY, UNRECOVERABLE_STATUS_CODE } from '../constants';
import { useClientStatusStore } from '../stores/useClientStatusStore';

import type { StatusCode } from '@kc-console/core';
import type { ServiceAppInitialState, ServiceAppRouteState, ServiceAppState } from '../constants';

/**
 * 서비스 앱의 공통 상태를 가져오는 훅입니다.
 * @description
 * - 서버사이드에서 가드의 결과로 반환된 상태를 base로 합니다.
 * - serviceKeyword과 status는 클라이언트 사이드에서 변경될 수 있습니다.
 * - serviceKeyword은 현재 라우터의 경로를 기반으로 가져옵니다.
 * - status는 서버 판정(routeState.status)과 클라이언트 판정(useClientStatusStore)의 병합값입니다.
 *   서버가 에러면 서버 판정을, 서버가 OK면 클라이언트 판정을 사용합니다.
 * - isUnauthorized는 axios 에러에 기반하여 모달을 출력할 때 사용됩니다.
 */
const useServiceAppState = () => {
  const router = useRouter();

  const { data: initialState } = useSuspenseQuery<ServiceAppInitialState>({
    // NOTE: 서버사이드에서 반환된 상태이므로 항상 존재하므로 useSuspenseQuery를 사용합니다.
    queryKey: [HOST_QUERY_KEY.GET_SERVICE_APP_INITIAL_STATE],
    queryFn: () => {
      return {} as ServiceAppInitialState;
    },
    staleTime: Infinity,
  });

  const { data: routeState } = useSuspenseQuery<ServiceAppRouteState>({
    // NOTE: 서버사이드에서 반환된 상태이므로 항상 존재하므로 useSuspenseQuery를 사용합니다.
    queryKey: [HOST_QUERY_KEY.GET_SERVICE_APP_ROUTE_STATE],
    queryFn: () => {
      return {} as ServiceAppRouteState;
    },
    staleTime: Infinity,
  });

  const clientStatus = useClientStatusStore(s => s.clientStatus);
  const setClientStatus = useClientStatusStore(s => s.setClientStatus);

  const status = useMemo(() => {
    // 시드가 없는 경로(비 gssp)는 서버 판정 부재이므로 OK(이의 없음)로 간주합니다.
    const serverStatus = routeState.status ?? STATUS_CODE.OK;
    const isServerError = serverStatus !== STATUS_CODE.OK;

    return isServerError ? serverStatus : clientStatus;
  }, [routeState.status, clientStatus]);

  /**
   * 클라이언트 판정 status를 기록합니다.
   * @description
   * 다음 세 경우에는 write가 무시되거나 화면에 반영되지 않습니다.
   * - 클라이언트 판정이 UNRECOVERABLE이면 write를 무시합니다.
   * - 서버 판정이 에러인 동안에는 write되어도 병합(서버 우선)에 가려 화면에 반영되지 않습니다.
   * - SSR 렌더 중에는 write를 무시합니다 (useClientStatusStore의 가드).
   */
  const setClientAppStatus = useCallback(
    (nextStatus: StatusCode) => {
      const { clientStatus: prevClientStatus } = useClientStatusStore.getState();

      if (UNRECOVERABLE_STATUS_CODE.includes(prevClientStatus)) return;

      setClientStatus(nextStatus);
    },
    [setClientStatus],
  );

  const serviceKeyword = useMemo(() => {
    // depth-1 경로(/dashboard?project_id=…)에서 쿼리스트링이 keyword에 섞이지 않도록 먼저 제거합니다.
    const pathname = router.asPath.split('?')[0];
    return pathname.split('/')[1] || '';
  }, [router.asPath]);

  return {
    state: {
      ...initialState,
      ...routeState,
      status,
      serviceKeyword,
    } as ServiceAppState,
    setClientAppStatus,
  };
};

export default useServiceAppState;
